import { useRef, type ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/shadcn/alert-dialog';
import type { VariantProps } from 'class-variance-authority';
import type { buttonVariants } from '../shadcn/button';

interface Props {
  isOpen: boolean;
  close: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  onUnmount?: () => void;
  title?: string;
  message: string;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  hideCancel?: boolean;
  confirmVariants?: VariantProps<typeof buttonVariants>;
  cancelVariants?: VariantProps<typeof buttonVariants>;
}

export function DefaultDialog({
  isOpen,
  close,
  onConfirm,
  onCancel,
  onUnmount,
  title = 'Error',
  message,
  confirmLabel = 'OK',
  confirmVariants,
  cancelVariants,
  cancelLabel = 'Cancel',
  hideCancel = false,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    onCancel?.();
    close();
  };

  const handleConfirm = () => {
    onConfirm?.();
    close();
  };

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount?.();
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent
        ref={contentRef}
        className="w-full max-w-lg"
        onAnimationEnd={handleAnimationEnd}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <AlertDialogTitle className="text-lg font-semibold">{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm text-zinc-600">
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          {!hideCancel && (
            <AlertDialogCancel onClick={handleCancel} {...cancelVariants}>
              {cancelLabel}
            </AlertDialogCancel>
          )}
          <AlertDialogAction onClick={handleConfirm} {...confirmVariants}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
